/**
 * EntityNode - Custom React Flow node component for schema graph.
 *
 * Conditional entities (those with a row_condition) are rendered with a
 * dashed border and a small "~" badge to signal that they are not created
 * for every row.
 */

import { Handle, type Node, type NodeProps, Position } from '@xyflow/react'
import { Anchor } from 'lucide-react'
import React from 'react'
import { cn } from '@/utils/cn'
import { getEntityIcon } from './helpers'
import type { EntityNodeData } from './types'

export const EntityNode = React.memo(function EntityNode({
  data,
  selected,
}: NodeProps<Node<EntityNodeData>>) {
  const Icon = getEntityIcon(data.entityType as string)
  const isConditional = Boolean(data.isConditional)
  const isAnchor = Boolean(data.isAnchor)

  return (
    <div
      className={cn(
        'px-4 py-3 rounded-lg border-2 shadow-lg min-w-[120px] relative',
        'bg-card text-card-foreground cursor-move',
        isConditional && 'border-dashed',
        isAnchor && 'ring-2 ring-amber-500',
        selected && 'ring-2 ring-info'
      )}
      style={{ borderColor: data.color as string }}
    >
      <Handle type="target" position={Position.Left} className="!bg-muted-foreground !w-2 !h-2" />

      {/* Anchor badge */}
      {isAnchor && (
        <span
          className="absolute -top-2 -left-2 text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center bg-amber-500 text-black shadow-lg"
          title="Anchor Node — links to existing entity in knowledge graph"
        >
          <Anchor className="h-3 w-3" />
        </span>
      )}

      {/* Conditional badge */}
      {isConditional && (
        <span
          className={cn(
            'absolute -top-2 text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center',
            isAnchor ? '-right-6' : '-right-2'
          )}
          style={{ backgroundColor: data.color as string, color: '#000' }}
          title="Conditional — only created for rows matching the row condition"
        >
          ~
        </span>
      )}

      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded" style={{ backgroundColor: `${data.color as string}20` }}>
          <Icon className="h-4 w-4" style={{ color: data.color as string }} />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{data.entityLabel as string}</span>
          <span className="text-xs text-muted-foreground">{data.entityType as string}</span>
        </div>
      </div>

      <Handle type="source" position={Position.Right} className="!bg-muted-foreground !w-2 !h-2" />
    </div>
  )
})

export const nodeTypes = {
  entityNode: EntityNode,
}
